import { Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { forwardRef } from 'react';

import { CenterSpinner } from '@revelio/core';
import {
  type Item,
  SelectionCategories,
  type SelectionListIdNames,
  type TreeItem,
} from '@revelio/iso-utility';

import {
  AdditionalReportSelectionCategories,
  type ReportSelectionCategories,
} from '../utils';
import {
  ReportEntityCompanyEditor,
  type ReportEntityCompanyEditorHandle,
  type ReportEntityCompanyEditorProps,
} from './report-entity-company-editor';

interface ReportEntityCompanySelectionsProps
  extends ReportEntityCompanyEditorProps {
  selectionLists: ReportSelectionCategories[];
  onSelections: (selections: Record<string, TreeItem>) => void;
}

const CompanyResultFilters: string[] = [
  AdditionalReportSelectionCategories.COMPANY_REPVUE,
  AdditionalReportSelectionCategories.COMPANY_ORGCHART,
];

const CompanyFilterNoResultsMessages: Record<string, string> = {
  [AdditionalReportSelectionCategories.COMPANY_ORGCHART]:
    'No companies with org chart data available for this search',
  [AdditionalReportSelectionCategories.COMPANY_REPVUE]:
    'No companies with RepVue data available for this search',
};

/**
 * ReportEntityCompanySelections
 *
 * This component wraps the <ReportEntityCompanyEditor /> to handle filtering
 * company search results for specific datasets, such as RepVue and the
 * OrgChart.
 */
export const ReportEntityCompanySelections = forwardRef<
  ReportEntityCompanyEditorHandle,
  Omit<ReportEntityCompanySelectionsProps, 'onSelectionsValue' | 'filterFn'>
>(({ selectionLists, onSelections, ...props }, ref) => {
  const companyFilter = selectionLists
    .filter((selectionList) => CompanyResultFilters.includes(selectionList))
    .pop();

  const {
    data: filterFn,
    isLoading,
    error,
  } = useQuery<((item: Item) => boolean) | undefined>({
    queryKey: ['companyData', companyFilter],
    queryFn: async () => {
      if (companyFilter) {
        const response = await fetch(
          `/assets/reports/company-filters/${companyFilter}.json`
        );
        if (!response.ok) {
          throw new Error(
            `Error loading company selections: ${response.statusText}`
          );
        }
        const jsonData = await response.json();
        if (!Array.isArray(jsonData)) {
          throw new Error('Expected company filter to be an array');
        }

        const rcidLookup = jsonData.reduce(
          (acc, rcid) => {
            acc[rcid] = true;
            return acc;
          },
          {} as Record<string, boolean>
        );

        return (item: Item) => !!rcidLookup[String(item.rcid ?? item.id)];
      }
      return undefined;
    },
    enabled: !!companyFilter,
  });

  if (isLoading || error) {
    return (
      <Flex minH="60px" justifyContent="center" alignItems="center">
        {isLoading && <CenterSpinner />}
        {error && <Text color="red.600">{error?.message}</Text>}
      </Flex>
    );
  }
  return (
    <ReportEntityCompanyEditor
      onSelectionsValue={(items) => {
        // Filtered company selection lists like repvue and orgchart must be mapped back to their original selection list id expected by the report entity
        const mappedSelections: Record<string, TreeItem> = {};
        const selectionListId = companyFilter
          ? companyFilter
          : SelectionCategories.COMPANY;
        Object.keys(items).forEach((key) => {
          const companyItem = items[key];
          // @ts-expect-error noUncheckedIndexedAccess
          const id = `${selectionListId}.${companyItem.rcid}`;
          mappedSelections[id] = {
            id: id,
            item: {
              // @ts-expect-error noUncheckedIndexedAccess
              id: companyItem.rcid,
              // @ts-expect-error noUncheckedIndexedAccess
              shortName: companyItem.primary_name,
              // @ts-expect-error noUncheckedIndexedAccess
              longName: companyItem.primary_name,
              // @ts-expect-error noUncheckedIndexedAccess
              label: companyItem.primary_name,
              // @ts-expect-error noUncheckedIndexedAccess
              ...(companyItem.primary_identifiers?.rics_k50
                ? {
                    rics_k50: String(companyItem?.primary_identifiers.rics_k50),
                    rics_k400: String(
                      companyItem?.primary_identifiers.rics_k400
                    ),
                    rics_k10: String(companyItem?.primary_identifiers.rics_k10),
                  }
                : {}),
              ...companyItem,
            },
            children: [],
            parentId: undefined,
            selectionListId: selectionListId as SelectionListIdNames,
          };
        });
        onSelections?.(mappedSelections);
      }}
      filterFn={filterFn}
      noFilteredResultsMessage={
        companyFilter
          ? CompanyFilterNoResultsMessages[companyFilter]
          : undefined
      }
      ref={ref}
      {...props}
    />
  );
});
